import FormModal from './FormModal';
import FileDownload from './FileDownload';
import FileUpload from './FileUpload';
import { Button, Modal, Segment, Table } from './';

import React from 'react';
import PropTypes from 'prop-types';
import { parse, unparse } from 'papaparse';

const CreateModal = ({ id, trigger, Form, defaultValues, meta, onCreate }) => {
  const Actions = ({ formProps, modal }) => (
    <Modal.Actions>
      <Button form={formProps.id} floated="right" positive>
        Create
      </Button>
      <Button onClick={modal.onCancel}>Cancel</Button>
    </Modal.Actions>
  );

  const onSubmit = values => onCreate({ ...defaultValues, ...values });

  return (
    <FormModal
      id={id}
      trigger={trigger}
      header="Create new" // FIXME: Use better term.
      Form={Form}
      Actions={Actions}
      defaultValues={defaultValues}
      meta={meta}
      onSubmit={onSubmit}
    />
  );
};

CreateModal.propTypes = {
  id: PropTypes.string.isRequired,
  trigger: PropTypes.element.isRequired,
  Form: PropTypes.func.isRequired,
  defaultValues: PropTypes.object.isRequired,
  meta: PropTypes.object,
  onCreate: PropTypes.func.isRequired,
};

// TODO: Detect changes while editing! And show a message/refresh?
const UpdateModal = ({
  id,
  trigger,
  Form,
  row,
  defaultValues,
  meta,
  onUpdateRow,
}) => {
  const Actions = ({ formProps, modal }) => (
    <Modal.Actions>
      <Button form={formProps.id} floated="right" positive>
        Update
      </Button>
      <Button onClick={modal.onCancel}>Cancel</Button>
      <Button onClick={modal.onReset} floated="left">
        Reset
      </Button>
    </Modal.Actions>
  );

  const onSubmit = values => onUpdateRow(row, values);

  const formDefaultValues = { ...defaultValues, ...row.currentValues };

  return (
    <FormModal
      id={id}
      trigger={trigger}
      header={`Update ${row.title}`}
      Form={Form}
      Actions={Actions}
      defaultValues={formDefaultValues}
      meta={meta}
      onSubmit={onSubmit}
    />
  );
};

UpdateModal.propTypes = {
  id: PropTypes.string,
  trigger: PropTypes.element.isRequired,
  Form: PropTypes.func.isRequired,
  row: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  onUpdateRow: PropTypes.func.isRequired,
};

const DestroyModal = ({ trigger, row, onDestroyRow }) => {
  const onClickDestroy = () => onDestroyRow(row);
  return (
    <Modal trigger={trigger} basic>
      <Modal.Header>Destroy?</Modal.Header>
      <Modal.Content>
        Do you really want to destroy {row.description}?
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClickDestroy} negative>
          Destroy
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

DestroyModal.propTypes = {
  trigger: PropTypes.element.isRequired,
  row: PropTypes.object.isRequired,
  onDestroyRow: PropTypes.func.isRequired,
};

const TableEditor = ({
  id,
  locked = false,
  Form: EditorForm,
  Columns: EditorColumns,
  Header: EditorHeader,
  Row: EditorRow,
  Actions: EditorActions = ({ children }) => children,
  rows,
  defaultValues,
  meta,
  actions,
}) => {
  const onUpdateRow = (row, values) => {
    actions.update(row.key, values);
  };

  const onDestroyRow = row => {
    actions.destroy(row.key);
  };

  const onExportContents = () => {
    const json = actions.export(rows);
    return [unparse(json)];
  };

  const onImportFile = file => {
    const fileReader = new FileReader();
    fileReader.onloadend = () => {
      const parsed = parse(fileReader.result, {
        header: true,
        skipEmptyLines: true,
      });
      actions.import(parsed.data);
    };
    fileReader.readAsText(file);
  };

  return (
    <section id={id}>
      <Segment attached="top">
        <EditorActions>
          {!locked && (
            <CreateModal
              id={`${id}--create-modal`}
              trigger={<Button primary>Create...</Button>}
              Form={EditorForm}
              defaultValues={defaultValues}
              meta={meta}
              onCreate={values => actions.create(values)}
            />
          )}
          {actions.export && (
            <FileDownload
              trigger={
                <Button
                  content="Export"
                  icon="upload"
                  floated="right"
                  basic
                  primary
                />
              }
              mimeType="text/csv; encoding=utf-8"
              fileName="nosturi-export.csv"
              onContents={onExportContents}
            />
          )}
          {!locked &&
            !!actions.import && (
              <FileUpload
                trigger={
                  <Button
                    content="Import"
                    icon="download"
                    floated="right"
                    basic
                    positive
                  />
                }
                onFile={onImportFile}
              />
            )}
        </EditorActions>
      </Segment>
      <Table fixed singleLine attached="bottom" compact>
        {EditorColumns && (
          <EditorColumns>
            <colgroup>
              <col style={{ width: 'calc(2 * 100%/16)' }} />
            </colgroup>
          </EditorColumns>
        )}
        <Table.Header>
          <EditorHeader>
            {!locked && <Table.HeaderCell width={2}>Actions</Table.HeaderCell>}
          </EditorHeader>
        </Table.Header>
        <Table.Body>
          {rows.map(row => (
            <EditorRow key={row.key} row={row} meta={meta}>
              {!locked && (
                <Table.Cell>
                  <UpdateModal
                    id={`${id}--${row.id}--update-modal`}
                    trigger={<Button icon="edit" basic circular compact />}
                    Form={EditorForm}
                    row={row}
                    meta={meta}
                    onUpdateRow={onUpdateRow}
                  />
                  <DestroyModal
                    id={`${id}--${row.id}--destroy-modal`}
                    trigger={
                      <Button icon="remove" basic circular compact negative />
                    }
                    Form={EditorForm}
                    row={row}
                    onDestroyRow={onDestroyRow}
                  />
                </Table.Cell>
              )}
            </EditorRow>
          ))}
        </Table.Body>
      </Table>
    </section>
  );
};

/* TODO: If we have onRowClear, display Clear button, if we have Create
add Create */

TableEditor.propTypes = {
  id: PropTypes.string.isRequired,
  locked: PropTypes.bool,
  Form: PropTypes.func.isRequired,
  Header: PropTypes.func.isRequired,
  Row: PropTypes.func.isRequired,
  Actions: PropTypes.func,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaultValues: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  ).isRequired,
  actions: PropTypes.shape({
    create: PropTypes.func,
    update: PropTypes.func,
    destroy: PropTypes.func,
  }).isRequired,
  meta: PropTypes.object,
};

export default TableEditor;



// WEBPACK FOOTER //
// ./src/components/TableEditor.js